import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type BonusEntry = { id: string; currency: string; amount: number; kind: string; status: string; reason: string | null; createdAt: string };

const CURRENCIES = ["CDF", "XOF", "XAF", "EUR"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function BonusPage() {
  const queryClient = useQueryClient();
  const [userId, setUserId] = React.useState("");
  const [currency, setCurrency] = React.useState("CDF");
  const [amount, setAmount] = React.useState("");
  const [reason, setReason] = React.useState("");
  const validUser = UUID.test(userId.trim());
  const validAmount = /^\d+$/.test(amount) && Number(amount) >= 1 && Number(amount) <= 10000000;
  const validReason = reason.trim().length >= 2 && reason.trim().length <= 200;

  const history = useQuery({
    queryKey: ["bonus-history", userId],
    enabled: validUser,
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/bonuses/${userId.trim()}`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load the bonus history");
      return ((await response.json()) as { history: BonusEntry[] }).history;
    },
  });

  const grant = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/bonuses`, {
        method: "POST",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ userId: userId.trim(), currency, amount: Number(amount), reason: reason.trim() }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Could not grant the bonus");
      return payload;
    },
    onSuccess: () => {
      toast.success("Bonus granted. The user can withdraw it without a code.");
      setAmount("");
      setReason("");
      queryClient.invalidateQueries({ queryKey: ["bonus-history", userId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Bonus Deuxda" description="Grant a bonus to a user. The user withdraws it to their payout method without a code." />

      <Card className="rounded-2xl">
        <CardContent className="space-y-6 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">User ID</Label>
              <Input value={userId} onChange={(e) => setUserId(e.target.value.trim())} placeholder="00000000-0000-0000-0000-000000000000" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Currency</Label>
              <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Amount</Label>
              <Input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.trim())} placeholder="2000" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Welcome gift" />
            </div>
          </div>
          {userId && !validUser && <p className="text-sm text-destructive">Enter a valid user ID.</p>}
          <Button className="rounded-xl" disabled={!validUser || !validAmount || !validReason || grant.isPending} onClick={() => grant.mutate()}>
            {grant.isPending ? "Granting…" : "Grant bonus"}
          </Button>
        </CardContent>
      </Card>

      {validUser && (
        <Card className="rounded-2xl">
          <CardContent className="space-y-3 p-6">
            <p className="text-sm font-semibold">History</p>
            {history.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {history.isError && <p className="text-sm text-destructive">The history could not be loaded.</p>}
            {history.data?.length === 0 && <p className="text-sm text-muted-foreground">No bonus yet.</p>}
            {history.data?.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between rounded-xl bg-muted/40 p-3 text-sm">
                <span>
                  {entry.kind === "grant" ? "Granted" : "Withdrawn"} · {entry.reason ?? "—"} · {new Date(entry.createdAt).toLocaleString("en-GB")}
                </span>
                <span className="font-semibold">
                  {entry.amount.toLocaleString("en-GB")} {entry.currency} <span className="text-xs text-muted-foreground">({entry.status})</span>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
